import { useEffect, useRef, useState } from "react";
import mesimoLogoMark from "../../../assets/logo/logo-watermark.png";

const services = ["Custom Software", "Web Application", "Mobile App", "Cloud & DevOps", "Full Project"];
const budgets = ["GH₵ 5k – 20k", "GH₵ 20k – 50k", "GH₵ 50k – 100k", "GH₵ 100k+", "Let's discuss"];
const formSubmitToken = (import.meta.env.VITE_CONTACT_FORM_TOKEN) as string;
const defaultContactEndpoint = `https://formsubmit.co/${formSubmitToken}`;
const contactEndpoint = (import.meta.env.VITE_CONTACT_FORM_ENDPOINT || defaultContactEndpoint) as string;

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [selectedBudget, setSelectedBudget] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const firstName = data.get("firstName")?.toString().trim() ?? "";
    const lastName = data.get("lastName")?.toString().trim() ?? "";
    const email = data.get("email")?.toString().trim() ?? "";
    const message = data.get("message")?.toString().trim() ?? "";

    const params = new URLSearchParams();
    params.append("name", `${firstName} ${lastName}`.trim());
    params.append("firstName", firstName);
    params.append("lastName", lastName);
    params.append("email", email);
    params.append("message", message);
    params.append("service", selectedService);
    params.append("budget", selectedBudget);
    params.append("_subject", `New inquiry from ${firstName} ${lastName}`.trim());
    params.append("_captcha", "false");
    params.append("_template", "table");

    setSubmitStatus("loading");
    if (!contactEndpoint) {
      setSubmitStatus("error");
      return;
    }

    try {
      const res = await fetch(contactEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitStatus("success");
        form.reset();
        setSelectedService("");
        setSelectedBudget("");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-24 md:py-36 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">Get In Touch</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-950 leading-tight">
              Let&apos;s Build<br />
              <span className="italic font-light text-gray-400">Together</span>
            </h2>
            <p className="text-gray-500 max-w-sm text-base leading-relaxed">
              Tell us about your project and we&apos;ll get back to you within 24 hours with our thoughts and next steps.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left info panel */}
          <div className={`lg:col-span-2 flex flex-col gap-6 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="bg-gray-950 rounded-2xl p-8 text-white flex-1">
              {/* Logo in contact card */}
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <img
                    src={mesimoLogoMark}
                    alt="Mesimo Inc."
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="font-bold text-lg tracking-tight">Mesimo<span className="font-light text-white/50">.inc</span></span>
              </div>

              <h3 className="font-bold text-xl mb-2">Let&apos;s Build Something Great</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Whether you have a detailed spec or just a rough idea — we love a good challenge. Our team is ready to turn your vision into a world-class software product.
              </p>

              <div className="space-y-5">
                {[
                  { icon: "ri-mail-line", label: "Email", value: "mesimo.inc@gmail.com" },
                  { icon: "ri-phone-line", label: "Phone", value: "+233 242 375 859" },
                  { icon: "ri-map-pin-2-line", label: "Based In", value: "Takoradi, Ghana" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                      <i className={`${item.icon} text-white text-base`}></i>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs">{item.label}</div>
                      <div className="text-white text-sm font-medium">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex gap-3">
                {[
                  { icon: "ri-instagram-line", href: "https://www.instagram.com/mesimo.inc" },
                  // { icon: "ri-twitter-x-line", href: "https://twitter.com/mesimoinc" },
                  // { icon: "ri-linkedin-line", href: "https://www.linkedin.com/company/mesimo-inc" },
                  { icon: "ri-github-line", href: "https://github.com/Mesimo-Labs" },
                ].map((item) => (
                  <a
                    key={item.icon}
                    href={item.href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { num: "24h", label: "Response time" },
                { num: "NDA", label: "On request" },
              ].map((item) => (
                <div key={item.label} className="bg-white rounded-2xl p-5 border border-gray-100">
                  <div className="text-2xl font-bold text-gray-950">{item.num}</div>
                  <div className="text-gray-400 text-xs mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100"
            >
              {submitStatus === "success" ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-emerald-500 text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-xl text-gray-950 mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">Our team will get back to you within 24 hours.</p>
                  <button
                    type="button"
                    onClick={() => setSubmitStatus("idle")}
                    className="mt-6 text-sm text-gray-500 underline cursor-pointer"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        placeholder="John"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        placeholder="Doe"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Service Needed</label>
                    <div className="flex flex-wrap gap-2">
                      {services.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSelectedService(s)}
                          className={`px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${selectedService === s
                            ? "bg-gray-950 text-white border-gray-950"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                            }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Budget Range</label>
                    <div className="flex flex-wrap gap-2">
                      {budgets.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setSelectedBudget(b)}
                          className={`px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${selectedBudget === b
                            ? "bg-gray-950 text-white border-gray-950"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                            }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Tell Us About Your Project</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="What are you building? What problem does it solve? Any timeline in mind?"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                  </div>

                  {submitStatus === "error" && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === "loading"}
                    className="w-full bg-gray-950 text-white font-semibold text-sm py-4 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitStatus === "loading" ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> Send Message to Mesimo Inc.</>
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
